import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getJamaahList, deleteJamaah } from '../services/jamaahService';

// Styles
const styles = {
  container: {
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '1.8rem',
    color: '#333',
    margin: '0',
  },
  addButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },
  filters: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  searchContainer: {
    display: 'flex',
    gap: '10px',
    flex: '1',
    maxWidth: '500px',
  },
  searchInput: {
    flex: '1',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  searchButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  statusFilter: {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  notification: {
    padding: '12px 16px',
    marginBottom: '20px',
    borderRadius: '4px',
  },
  success: {
    backgroundColor: '#dff0d8',
    color: '#3c763d',
    border: '1px solid #d6e9c6',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 8px',
    backgroundColor: '#f2f2f2',
    borderBottom: '2px solid #ddd',
  },
  td: {
    padding: '12px 8px',
    borderBottom: '1px solid #ddd',
  },
  actionButton: {
    marginRight: '8px',
    padding: '4px 8px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  detailButton: {
    backgroundColor: '#2196F3',
    color: 'white',
  },
  editButton: {
    backgroundColor: '#FFC107',
    color: 'black',
  },
  deleteButton: {
    backgroundColor: '#F44336',
    color: 'white',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '20px',
  },
  pageInfo: {
    color: '#666',
  },
  pageButtons: {
    display: 'flex',
    gap: '8px',
  },
  pageButton: {
    padding: '4px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  activePageButton: {
    backgroundColor: '#2196F3',
    color: 'white',
    border: '1px solid #2196F3',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  emptyStateMessage: {
    marginBottom: '20px',
  },
};

// Component untuk Jamaah List Page
function JamaahListPage() {
  // State
  const [jamaahList, setJamaahList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Get location for notifications from redirect (e.g. after form submission)
  const location = useLocation();

  // Load data on component mount and when filters/pagination change
  useEffect(() => {
    fetchJamaahList();
  }, [page, perPage, searchQuery, statusFilter]);

  // Check for notifications from navigation state
  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);
      
      // Clear notification after 5 seconds
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Fetch jamaah list from API
  const fetchJamaahList = async () => {
    setLoading(true);
    try {
      const response = await getJamaahList(page, perPage, searchQuery, statusFilter);
      
      if (response && response.data) {
        setJamaahList(response.data);
        
        // Set pagination data
        if (response.meta) {
          setTotalPages(response.meta.totalPages || 1);
          setTotalItems(response.meta.total || 0);
        }
      }
      setError(null);
    } catch (err) {
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
      setJamaahList([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setPage(1); // Reset to first page on new search
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1); // Reset to first page on filter change
  };

  // Handle page change
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Handle jamaah deletion
  const handleDeleteJamaah = async (id, name) => {
    if (window.confirm(`Anda yakin ingin menghapus data jamaah "${name}"?`)) {
      try {
        await deleteJamaah(id);
        setNotification({ 
          type: 'success', 
          message: 'Data jamaah berhasil dihapus' 
        });
        fetchJamaahList(); // Refresh list
      } catch (err) {
        setNotification({ 
          type: 'error', 
          message: 'Gagal menghapus data: ' + (err.message || 'Unknown error') 
        });
      }
    }
  };

  // Render notification if present
  const renderNotification = () => {
    if (!notification) return null;
    
    const notificationStyle = {
      ...styles.notification,
      ...(notification.type === 'success' ? styles.success : styles.error)
    };
    
    return (
      <div style={notificationStyle}>
        {notification.message}
      </div>
    );
  };

  // Render loading spinner
  const renderLoading = () => (
    <div style={styles.loadingSpinner}>
      Loading data...
    </div>
  );

  // Render empty state
  const renderEmptyState = () => (
    <div style={styles.emptyState}>
      <p style={styles.emptyStateMessage}>
        {searchQuery || statusFilter 
          ? 'Tidak ada jamaah yang sesuai dengan kriteria pencarian.' 
          : 'Tidak ada data jamaah. Mulai dengan menambahkan jamaah baru.'}
      </p>
      <Link to="/jamaah/tambah" style={styles.addButton}>
        Tambah Jamaah Baru
      </Link>
    </div>
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // Render jamaah table
  const renderJamaahTable = () => (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={styles.th}>Nama</th>
          <th style={styles.th}>NIK</th>
          <th style={styles.th}>Jenis Kelamin</th>
          <th style={styles.th}>Kontak</th>
          <th style={styles.th}>Status</th>
          <th style={styles.th}>Tanggal Daftar</th>
          <th style={styles.th}>Aksi</th>
        </tr>
      </thead>
      <tbody>
        {jamaahList.map((jamaah) => (
          <tr key={jamaah.id}>
            <td style={styles.td}>{jamaah.nama_lengkap}</td>
            <td style={styles.td}>{jamaah.nik}</td>
            <td style={styles.td}>{jamaah.jenis_kelamin}</td>
            <td style={styles.td}>
              {jamaah.nomor_telepon || '-'}<br />
              <small>{jamaah.email || '-'}</small>
            </td>
            <td style={styles.td}>
              <span style={{
                padding: '3px 8px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                backgroundColor: jamaah.status === 'Aktif' ? '#e8f5e9' : '#ffebee',
                color: jamaah.status === 'Aktif' ? '#2e7d32' : '#c62828'
              }}>
                {jamaah.status}
              </span>
            </td>
            <td style={styles.td}>
              {formatDate(jamaah.created_at)}
            </td>
            <td style={styles.td}>
              <Link 
                to={`/jamaah/${jamaah.id}`} 
                style={{...styles.actionButton, ...styles.detailButton, textDecoration: 'none'}}
              >
                Detail
              </Link>
              <Link 
                to={`/jamaah/${jamaah.id}/edit`} 
                style={{...styles.actionButton, ...styles.editButton, textDecoration: 'none'}}
              >
                Edit
              </Link>
              <button 
                style={{...styles.actionButton, ...styles.deleteButton}}
                onClick={() => handleDeleteJamaah(jamaah.id, jamaah.nama_lengkap)}
              >
                Hapus
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Render pagination
  const renderPagination = () => (
    <div style={styles.pagination}>
      <div style={styles.pageInfo}>
        Menampilkan {jamaahList.length} dari {totalItems} jamaah
      </div>
      <div style={styles.pageButtons}>
        <button 
          style={styles.pageButton}
          onClick={() => goToPage(page - 1)}
          disabled={page === 1}
        >
          &laquo; Prev
        </button>
        {[...Array(totalPages).keys()].map((idx) => (
          <button
            key={idx}
            style={{
              ...styles.pageButton,
              ...(page === idx + 1 ? styles.activePageButton : {})
            }}
            onClick={() => goToPage(idx + 1)}
          >
            {idx + 1}
          </button>
        ))}
        <button 
          style={styles.pageButton}
          onClick={() => goToPage(page + 1)}
          disabled={page === totalPages}
        >
          Next &raquo;
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Daftar Jamaah</h1>
        <Link to="/jamaah/tambah" style={styles.addButton}>
          + Tambah Jamaah Baru
        </Link>
      </div>
      
      {renderNotification()}
      
      <div style={styles.filters}>
        <form onSubmit={handleSearch} style={styles.searchContainer}>
          <input
            type="text"
            placeholder="Cari berdasarkan nama, NIK, atau email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={styles.searchInput}
          />
          <button type="submit" style={styles.searchButton}>
            Cari
          </button>
        </form>
        
        <select
          value={statusFilter}
          onChange={handleStatusFilterChange}
          style={styles.statusFilter}
        >
          <option value="">Semua Status</option>
          <option value="Aktif">Aktif</option>
          <option value="Non-Aktif">Non-Aktif</option>
        </select>
      </div>
      
      {error && (
        <div style={{...styles.notification, ...styles.error}}>
          {error}
        </div>
      )}
      
      {loading ? (
        renderLoading()
      ) : jamaahList.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          {renderJamaahTable()}
          {renderPagination()}
        </>
      )}
    </div>
  );
}

export default JamaahListPage;